// =============================================================================
// MeuDriver — StudentPremium
// Landing page da assinatura Premium do aluno.
// Rota: /aluno/premium
// =============================================================================

import { useSearchParams } from 'react-router-dom'
import { Crown, Percent, TrendingUp, BadgeCheck, Loader2, CheckCircle } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { StudentPremiumCard } from '@/components/student/StudentPremiumCard'
import { useSubscription, useCreateSubscription } from '@/hooks/useSubscription'

const BENEFITS = [
  {
    icon: Percent,
    title: '10% de desconto em todas as aulas',
    description: 'O desconto é aplicado automaticamente em toda aula que você agendar, com qualquer instrutor.',
  },
  {
    icon: TrendingUp,
    title: 'Prioridade na lista de espera',
    description: 'Quando um instrutor concorrido abre novos horários, você é avisado na hora — os demais alunos, 2 horas depois.',
  },
  {
    icon: BadgeCheck,
    title: 'Badge "Premium" no perfil',
    description: 'Destaque no seu perfil e nas avaliações que você deixa.',
  },
]

export default function StudentPremium() {
  const [searchParams] = useSearchParams()
  const { data: subscription, isLoading: subLoading } = useSubscription()
  const createSubscription = useCreateSubscription()

  // Feedback na volta do checkout do Asaas
  useEffect(() => {
    const status = searchParams.get('status')
    if (status === 'success') {
      toast.success('Assinatura iniciada! Aguardando confirmação do pagamento.')
    } else if (status === 'cancelled' || status === 'expired') {
      toast.info('O pagamento não foi concluído. Você pode tentar de novo quando quiser.')
    }
  }, [searchParams])

  const isActive = subscription?.status === 'active'

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mx-auto">
          <Crown className="h-8 w-8 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">MeuDriver Premium</h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Aproveite ao máximo suas aulas com benefícios exclusivos por apenas
          <span className="font-semibold text-gray-900"> R$29,90/mês</span>.
        </p>
      </div>

      {/* Status card (se já assinou) */}
      {!subLoading && isActive && (
        <div className="flex items-center gap-2 text-sm text-green-700 font-medium bg-green-50 border border-green-100 rounded-lg px-4 py-3">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Você é Premium! Seus benefícios estão ativos.
        </div>
      )}

      {/* Benefícios */}
      <div className="space-y-3">
        {BENEFITS.map((b) => (
          <Card key={b.title} className="border-amber-100">
            <CardContent className="flex items-start gap-3 pt-4">
              <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
                <b.icon className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{b.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Assinatura ativa: mostra card de gerenciamento */}
      {isActive ? (
        <StudentPremiumCard />
      ) : (
        <Button
          className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold h-12"
          onClick={() => createSubscription.mutate()}
          disabled={createSubscription.isPending || subLoading}
          data-testid="subscribe-premium-btn"
        >
          {createSubscription.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Crown className="h-4 w-4 mr-2" />
              Assinar por R$29,90/mês
            </>
          )}
        </Button>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Cancele a qualquer momento. Sem multa ou fidelidade.
      </p>
    </div>
  )
}
